import * as Sentry from "@sentry/nextjs";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MonitoringUser {
  id: string;
  username: string;
  email?: string;
  role?: string;
  branch?: string;
}

export interface CaptureContext {
  tags?: Record<string, string | number | boolean>;
  extra?: Record<string, unknown>;
  level?: "fatal" | "error" | "warning" | "info" | "debug";
}

// ─── User Identification ───────────────────────────────────────────────────────

export function identifyUser(user: MonitoringUser): void {
  Sentry.setUser({
    id: user.id,
    username: user.username,
    email: user.email,
    role: user.role,
    branch: user.branch,
  } as Sentry.User & Record<string, unknown>);
}

export function clearUser(): void {
  Sentry.setUser(null);
}

// ─── Manual Error Capture ─────────────────────────────────────────────────────

export function captureException(
  error: unknown,
  context?: CaptureContext
): void {
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, String(value));
      });
    }
    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    if (context?.level) {
      scope.setLevel(context.level);
    }
    Sentry.captureException(error);
  });
}

export function captureMessage(
  message: string,
  context?: CaptureContext
): void {
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, String(value));
      });
    }
    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    scope.setLevel(context?.level ?? "info");
    Sentry.captureMessage(message);
  });
}

export function addBreadcrumb(
  message: string,
  category: string = "app",
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({ message, category, data, level: "info" });
}
